import { create } from "zustand";
import { appSettingsConfig, loanFieldsMap } from "@/lib/constants";
import { loan_type } from "@/lib/constants";

export interface LoanType {
	option: string;
	id: number;
	value: string;
}

export interface FormState {
	formError: boolean;
	selectedIndex: number;
	isLoading: boolean;
	isPhoneVerified: boolean;
	enabledFields: Record<string, any[]>;
	enabledLoans: LoanType[];
	loanAmountMin: number;
	isSubmitted: boolean;
	validateFormDetails?: () => boolean;
	appSettings: Record<string, string>;
	formContainer: number;
	toggleTC: boolean;
	toggleFranchise: boolean;
	// Setter functions
	setFormError: (hasError: boolean) => void;
	setSelectedIndex: (index: number) => void;
	setLoading: (loading: boolean) => void;
	setPhoneVerified: (isVerified: boolean) => void;
	setEnabledFields: (loanType: string) => void;
	setLoanAmountMin: (loanAmountMin: number) => void;
	setEnabledLoans: (loanTypes: string[]) => void;
	setIsSubmitted: (isSubmitted: boolean) => void;
	setValidateFormDetails: (fn: () => boolean) => void;
	setAppSettings: (appSettings: string) => void;
	setFormContainer: (size: number) => void;
	setToggleTC: (toggle: boolean) => void;
	setToggleFranchise: (toggle: boolean) => void;
}

export const useFormState = () =>
	create<FormState>((set) => ({
		formError: false,
		selectedIndex: 0,
		isLoading: false,
		isPhoneVerified: false,
		enabledFields: {}, // Initially empty but will update dynamically
		enabledLoans: [], // Initially empty but will update dynamically
		loanAmountMin: 10000,
		isSubmitted: false,
		appSettings: {},
		formContainer: 0,
		toggleTC: false,
		toggleFranchise: false,

		setFormError: (hasError) => set(() => ({ formError: hasError })),
		setSelectedIndex: (index) => set(() => ({ selectedIndex: index })),
		setLoading: (loading) => set(() => ({ isLoading: loading })),
		setPhoneVerified: (isVerified) => set(() => ({ isPhoneVerified: isVerified })),
		setEnabledFields: (loanType) => {
			set({ enabledFields: loanFieldsMap[loanType] || {} });
		},
		setEnabledLoans: (loanTypes) => {
			if (!Array.isArray(loanTypes)) {
				console.warn("setEnabledLoans: Expected an array, but got: ", loanTypes);
				return;
			}

			const filteredLoans = loan_type.filter(
				(loan) => loanTypes.includes(loan.value) // Match loan types from settings
			);

			set({ enabledLoans: filteredLoans }); // Store full loan objects (option, id, value)
		},
		setLoanAmountMin: (loanAmount) => set(() => ({ loanAmountMin: loanAmount })),
		setIsSubmitted: (status) => set(() => ({ isSubmitted: status })),
		setValidateFormDetails: (fn) => set(() => ({ validateFormDetails: fn })),
		setAppSettings: (siteTitle) =>
			set(() => ({ appSettings: appSettingsConfig[siteTitle] || {} })),
		setFormContainer: (size) => set(() => ({ formContainer: size })),
		setToggleTC: (toggle) => set(() => ({ toggleTC: toggle })),
		setToggleFranchise: (toggle) => set(() => ({ toggleFranchise: toggle })),
	}));
